import axios from 'axios';
import { queryClient } from '#client/utils/queryClient.js';

const API_URL = import.meta.env.VITE_API_URL;

// Purchase Order Query Options
const purchaseOrderQueryOptions = {
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
};

// Vendor Filter Query Options
const uniqueVendorQueryOptions = {
    staleTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
};

const getPurchaseOrdersQueryOptions = (filters = {}, sortBy = 'poNumber', sortDirection = 'desc', page = 1, itemsPerPage = 20) => ({
    queryKey: ['purchase-orders', { page, itemsPerPage, filters, sortBy, sortDirection }],
    ...purchaseOrderQueryOptions,
});

const getPurchaseOrderQueryOptions = (poId) => ({
    queryKey: ['purchase-order', poId],
    enabled: !!poId,
    ...purchaseOrderQueryOptions,
});

export const purchaseOrderService = {
    /**
     * Builds URL parameters for API requests
     */
    buildApiParams: (page, itemsPerPage, filters = {}, UTCOffset) => {
        const params = new URLSearchParams({
            skip: String((page - 1) * itemsPerPage),
            take: String(itemsPerPage),
        });

        params.set('UTCOffset', String(UTCOffset));

        // Add search and filter parameters
        if (filters.queryValue) params.set('search', filters.queryValue);
        if (filters.poNumberFilter) params.set('poNumber', filters.poNumberFilter);
        if (Array.isArray(filters.vendorFilter) && filters.vendorFilter.length > 0) params.set('vendor', filters.vendorFilter.join(','));
        if (Array.isArray(filters.statusFilter) && filters.statusFilter.length > 0) params.set('status', filters.statusFilter.join(','));
        if (Array.isArray(filters.locationFilter) && filters.locationFilter.length > 0) params.set('location', filters.locationFilter.join(','));
        if (filters.startDateFilter) params.set('startDate', filters.startDateFilter);
        if (filters.endDateFilter) params.set('endDate', filters.endDateFilter);
        if (filters.expectedShipDateFromFilter) params.set('expectedShipDateFrom', filters.expectedShipDateFromFilter);
        if (filters.expectedShipDateToFilter) params.set('expectedShipDateTo', filters.expectedShipDateToFilter);
        if (filters.cancelDateFromFilter) params.set('cancelDateFrom', filters.cancelDateFromFilter);
        if (filters.cancelDateToFilter) params.set('cancelDateTo', filters.cancelDateToFilter);
        if (filters.qtyOrderedMin) params.set('qtyOrderedMin', filters.qtyOrderedMin);
        if (filters.qtyOrderedMax) params.set('qtyOrderedMax', filters.qtyOrderedMax);
        if (filters.qtyReceivedMin) params.set('qtyReceivedMin', filters.qtyReceivedMin);
        if (filters.qtyReceivedMax) params.set('qtyReceivedMax', filters.qtyReceivedMax);
        if (filters.qtyReceivedInDraftMin) params.set('qtyReceivedInDraftMin', filters.qtyReceivedInDraftMin);
        if (filters.qtyReceivedInDraftMax) params.set('qtyReceivedInDraftMax', filters.qtyReceivedInDraftMax);
        if (filters.qtySubmittedMin) params.set('qtySubmittedMin', filters.qtySubmittedMin);
        if (filters.qtySubmittedMax) params.set('qtySubmittedMax', filters.qtySubmittedMax);

        // Add sorting parameters
        if (filters.sortColumn !== undefined && filters.sortDirection) {
            params.set('sortColumn', String(filters.sortColumn));
            params.set('sortDirection', filters.sortDirection);
        }

        return params;
    },

    /**
     * Fetches paginated purchase orders from API using react-query
     */
    async fetchPaginatedPurchaseOrders(page = 1, itemsPerPage = 20, filters = {}) {
        const UTCOffset = new Date().getTimezoneOffset() / 60;
        const response = await queryClient.fetchQuery({
            ...getPurchaseOrdersQueryOptions(filters, filters.sortBy, filters.sortDirection, page, itemsPerPage),
            queryFn: async () => {
                const params = this.buildApiParams(page, itemsPerPage, filters, UTCOffset);
                const response = await axios.get(`${API_URL}/api/app/purchase-orders`, { params });

                const wrapper = response.data?.data ?? {};
                const items = Array.isArray(wrapper.data) ? wrapper.data : [];
                const total = typeof wrapper.total === 'number' ? wrapper.total : items.length;

                return {
                    rows: items,
                    total,
                };
            },
        });
        return response;
    },

    async fetchUniqueFilterOptions(table, column) {
        const response = await queryClient.fetchQuery({
            queryKey: ['unique-filter-options', table, column],
            ...uniqueVendorQueryOptions,
            queryFn: async () => {
                const response = await axios.get(`${API_URL}/api/app/purchase-orders/get-unique-filter-options`, {
                    params: {
                        table,
                        column,
                    },
                });
                return response.data;
            },
        });
        return response;
    },

    async exportPurchaseOrders(filters = {}) {
        const UTCOffset = new Date().getTimezoneOffset() / 60;
        const params = this.buildApiParams(1, -1, filters, UTCOffset);
        const response = await axios.get(`${API_URL}/api/app/purchase-orders`, { params });

        return response;
    },

    /**
     * Fetches a single purchase order by ID using react-query
     */
    async fetchPurchaseOrder(poId) {
        const response = await queryClient.fetchQuery({
            ...getPurchaseOrderQueryOptions(poId),
            queryFn: async () => {
                const response = await axios.get(`${API_URL}/api/app/purchase-orders/${poId}`);
                return response.data;
            },
        });
        return response;
    },

    /**
     * Updates a purchase order
     */
    async updatePurchaseOrder(poId, updateData) {
        const response = await axios.post(`${API_URL}/api/app/purchase-orders/${poId}/update`, updateData, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Invalidate related queries after update
        queryClient.invalidateQueries({ queryKey: ['purchase-order', poId] });
        queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });

        return response.data;
    },
};

// Export query options for use in components that need direct access
export { getPurchaseOrdersQueryOptions, getPurchaseOrderQueryOptions };
